import Immutable from 'seamless-immutable';
import pathOr from 'ramda/src/pathOr';
import mergeDeepLeft from 'ramda/src/mergeDeepLeft';
import groupBy from 'ramda/src/groupBy';
import { OFFER_STATUS_INDICATOR_STATUS } from 'invision-core/src/constants/status.constants';
import { CODES } from 'invision-core/src/components/metadata/codes/codes.constants';
import { MetadataCodeSelector, MetadataCodeTypeDictionarySelector, MetadataCodeTypeIntegerSelector } from 'invision-core/src/components/metadata/codes/codes.selectors';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import { DiscountsMetadataMapSelector } from 'invision-core/src/components/metadata/discounts/discounts.selectors';
import { getBoolOrDefault } from 'invision-core/src/components/helpers/bool.helper';
import { PRODUCT_CLASSIFICATIONS } from 'invision-core/src/constants/product.constants';
import { OfferingMetadataPricingPlansSelector } from 'invision-core/src/components/metadata/offerings/offerings.selectors';
import { IsDbss } from 'invision-core/src/components/session/businessunit.selectors';
import { BILLER_RULE_INSTANCE_TYPE, DEVICE_ADDON_SERVICE_ATTRIBUTE_ID } from '../../customercare.constants';
import { HasSuspendResumePermissionSelector, IsServiceLifeCycleStatusUpdatableSelector, selectedCustomerBulkItemsSelector, ServicesWithFormattedServiceAttributeSelector } from './services.selectors';
import { IsCurrentAccountPostpaidSelector } from './customer.convergent.biller.selectors';
import { ChangeOfferEligibilityRulesSelector, IsCoolingPeriodEnabledSelector } from './customer.offerings.selectors';
import { getActiveBillerTypeAmounts, getDiscountAndBriDetails, getServiceMoreActionMenuItems } from './selected.service.details.selectors.helper';
import { filterBulkOfferOptions, getOptionsByActiveBriType, hasPendingChange as hasOfferPendingChanges, isPaymentInstrumentChangeable } from '../../components/customer/dashboard/dbssDashboard/offering/offering.component.helper';
import { getMoreMenuItems } from './dashboard.selectors.helper';
import { getFormattedServiceAttributeValue } from './services.list.selectors.helper';
import { OFFERING_OPTION_STATUSES } from '../../components/shared/constants/offering.option.status.constants';
import clone from 'ramda/src/clone';
import { SelectedSubscriberOfferingInstanceIdSelector } from './search.subscriber.offerings.selectors';
import { IsPIIDataAccessEnabledSelector } from './customer.selectors';
var EMPTY_ARRAY = Immutable([]);
export var SelectedServiceIdentifierSelector = createImmutableSelector([selectedCustomerBulkItemsSelector, ServicesWithFormattedServiceAttributeSelector], function (bulkItems, services) {
  var serviceIdentifier = pathOr(null, ['data', 'selectedServiceIdentifier'], bulkItems);
  return serviceIdentifier || pathOr(null, ['serviceThumbnails', '0', 'ServiceIdentifier'], services);
});
export var SelectedServiceThumbnailSelector = createImmutableSelector([SelectedServiceIdentifierSelector, ServicesWithFormattedServiceAttributeSelector], function (serviceIdentifier, services) {
  return (services.serviceThumbnails || EMPTY_ARRAY).find(function (service) {
    return service.ServiceIdentifier === serviceIdentifier;
  });
});
export var IsFetchingServiceDetailsSelector = createImmutableSelector([selectedCustomerBulkItemsSelector], function (bulkItems) {
  return bulkItems.isFetchingServiceDetails;
});
export var SelectedServiceDetailsSelector = createImmutableSelector([SelectedServiceIdentifierSelector, selectedCustomerBulkItemsSelector, HasSuspendResumePermissionSelector, IsServiceLifeCycleStatusUpdatableSelector, MetadataCodeSelector(CODES.ServiceLifeCycleStatus), MetadataCodeSelector(CODES.UnitsOfMeasure), IsPIIDataAccessEnabledSelector], function (serviceIdentifier, bulkItems, hasSuspendResumeAccess, isServiceLifeCycleStatusUpdatable, serviceLifeCycleStatusCodes, units, isPIIDataAccessEnabled) {
  var serviceDetails = pathOr(EMPTY_ARRAY, ['data', 'selectedServices', 'data'], bulkItems).find(function (service) {
    return service.ServiceIdentifier.Value === serviceIdentifier;
  });
  return serviceDetails && Object.assign({}, serviceDetails, {
    ServiceLifeCycleStatusDetails: serviceDetails.ServiceLifeCycleStatus ? pathOr(EMPTY_ARRAY, ['items'], serviceLifeCycleStatusCodes).find(function (lifeCycleStatus) {
      return lifeCycleStatus.Value === serviceDetails.ServiceLifeCycleStatus;
    }) : undefined,
    moreActionMenuItems: getServiceMoreActionMenuItems(serviceDetails.Status, serviceDetails.AccountDetailsOfferings[0].IsEditable, hasSuspendResumeAccess, isServiceLifeCycleStatusUpdatable, isPIIDataAccessEnabled),
    UsageCaps: (serviceDetails.UsageCaps || []).map(function (item) {
      var _units$items$find;
      var usageCapItem = clone(item);
      usageCapItem.ThresholdType.Name = (_units$items$find = units.items.find(function (unitItem) {
        var _item$ThresholdType;
        return (item === null || item === void 0 ? void 0 : (_item$ThresholdType = item.ThresholdType) === null || _item$ThresholdType === void 0 ? void 0 : _item$ThresholdType.Code) && unitItem.Value === item.ThresholdType.Code.toString();
      })) === null || _units$items$find === void 0 ? void 0 : _units$items$find.Name;
      return usageCapItem;
    })
  });
});
export var SelectedServiceDetailsOfferOrderedOffering = createImmutableSelector([SelectedServiceDetailsSelector], function (selectedServiceDetails) {
  var _selectedServiceDetai;
  return selectedServiceDetails === null || selectedServiceDetails === void 0 ? void 0 : (_selectedServiceDetai = selectedServiceDetails.AccountDetailsOfferings) === null || _selectedServiceDetai === void 0 ? void 0 : _selectedServiceDetai[0];
});
export var SelectedServiceDetailsOfferOrderedOfferingAdditionalPropertiesSelector = createImmutableSelector([SelectedServiceDetailsOfferOrderedOffering], function (accountDetailOffering) {
  return (accountDetailOffering === null || accountDetailOffering === void 0 ? void 0 : accountDetailOffering.OrderedOfferingAdditionalProperties) || [];
});
export var SelectedServicePolicyCountersSelector = createImmutableSelector([SelectedServiceDetailsSelector], function (selectedServiceDetails) {
  var policyCounterList = pathOr([], ['PolicyCounters'], selectedServiceDetails);
  policyCounterList = policyCounterList.map(function (policyCounter) {
    if (policyCounter.PolicyRange && policyCounter.PolicyRange.length) {
      var policyRange = policyCounter.PolicyRange.map(function (range) {
        return mergeDeepLeft(range, {
          unitCode: policyCounter.BalanceUnitCode,
          currentStatus: policyCounter.PolicyStatus
        });
      });
      return mergeDeepLeft(policyCounter, {
        policyRange: policyRange
      });
    }
    return policyCounter;
  });
  var groupedPolicyCounter = groupBy(function (policyCounter) {
    return policyCounter.ExternalPolicyIdentifier;
  });
  return groupedPolicyCounter(policyCounterList);
});
export var SelectedServiceCurrencyCodeSelector = createImmutableSelector([selectedCustomerBulkItemsSelector], function (bulkItems) {
  return pathOr(null, ['data', 'selectedServices', 'currencyCode'], bulkItems);
});
export var SelectedServiceAssociatedDeviceFinanceDetailsSelector = createImmutableSelector([SelectedServiceDetailsSelector], function (service) {
  return pathOr(null, ['SubscriberDeviceFinanceDetails', '0'], service);
});
export var SelectedServiceAssociatedDeviceFinanceDiscountsSelector = createImmutableSelector([SelectedServiceAssociatedDeviceFinanceDetailsSelector, DiscountsMetadataMapSelector, SelectedServiceCurrencyCodeSelector], function (deviceFinancingDetails, allDiscounts, currency) {
  var discounts = [];
  if (allDiscounts && deviceFinancingDetails && deviceFinancingDetails.FinanceDiscounts && deviceFinancingDetails.FinanceDiscounts.length) {
    discounts = deviceFinancingDetails.FinanceDiscounts.map(function (financeDiscount) {
      if (allDiscounts[financeDiscount.DiscountId]) {
        return {
          Name: allDiscounts[financeDiscount.DiscountId].StorefrontText || allDiscounts[financeDiscount.DiscountId].Name,
          Currency: currency,
          savings: financeDiscount.Amount
        };
      }
    });
  }
  return discounts.filter(function (discount) {
    return discount;
  });
});
export var SelectedServiceAssociatedOfferDetailsSelector = createImmutableSelector([SelectedServiceDetailsSelector, IsDbss, IsCurrentAccountPostpaidSelector, MetadataCodeTypeIntegerSelector(CODES.TransitionConfiguration), ChangeOfferEligibilityRulesSelector, IsCoolingPeriodEnabledSelector, SelectedServiceCurrencyCodeSelector, DiscountsMetadataMapSelector, OfferingMetadataPricingPlansSelector, IsPIIDataAccessEnabledSelector], function (service, isDbss, isCurrentAccountPostpaid, transitionConfiguration, changeOfferEligibilityRules, isCoolingPeriodEnabledForBU, currencyCode, discountsMetadataMap, pricingPlanThumbnails, isPIIDataAccessEnabled) {
  var accountDetailsOfferings = pathOr([], ['AccountDetailsOfferings'], service);
  var offerDetails = accountDetailsOfferings.find(function (accountDetailsOffering) {
    return accountDetailsOffering.Status !== OFFER_STATUS_INDICATOR_STATUS.REMOVED;
  }) || accountDetailsOfferings.find(function (accountDetailsOffering) {
    return accountDetailsOffering.Status === OFFER_STATUS_INDICATOR_STATUS.REMOVED && accountDetailsOffering.IsCoolingOff;
  });
  if (!offerDetails) {
    return null;
  }
  var offer = Object.assign({}, offerDetails, {
    Options: offerDetails.Options.filter(function (option) {
      return option.BillerTypeAmounts && option.BillerTypeAmounts.length;
    })
  });
  var transitionProperties = pathOr(null, ['0', 'AdditionalProperties'], transitionConfiguration, false);
  var transitionFlagFromCodeTable = transitionProperties && transitionProperties.find(function (item) {
    return item.Key === 'AllowTransitioning';
  });
  var allowTransitioning = getBoolOrDefault(transitionFlagFromCodeTable ? transitionFlagFromCodeTable.Value : undefined, false);
  var briOptions = getOptionsByActiveBriType(offer, PRODUCT_CLASSIFICATIONS.PRODUCT);
  var hasSubscriptions = briOptions[BILLER_RULE_INSTANCE_TYPE.SUBSCRIPTION].length;
  var hasPendingChange = hasOfferPendingChanges(offer);
  var bulkOffer = filterBulkOfferOptions(offer);
  var canChangePaymentInstrument = isPaymentInstrumentChangeable(offer);
  var isInGracePeriod = offer.Options.some(function (option) {
    return option.OffCycleDetail && option.OffCycleDetail.GracePeriodExpiryDate && option.OffCycleDetail.CurrentRenewalFailedAttempts;
  });
  return Object.assign({}, offer, {
    moreOptionsMenuItems: getMoreMenuItems(hasSubscriptions, allowTransitioning, offer, bulkOffer, changeOfferEligibilityRules, canChangePaymentInstrument, isInGracePeriod, true, pricingPlanThumbnails, true, true, isPIIDataAccessEnabled),
    Options: offer.Options.map(function (option) {
      return Object.assign({}, option, {
        discountAndBriDetails: getDiscountAndBriDetails(option, currencyCode, discountsMetadataMap)
      });
    })
  });
});
export var SelectedServiceAssociatedServiceFeaturesSelector = createImmutableSelector([SelectedServiceDetailsSelector], function (service) {
  return (service && service.ServiceFeatures || EMPTY_ARRAY).filter(function (serviceFeature) {
    return serviceFeature.Status !== OFFERING_OPTION_STATUSES.REMOVED;
  }).map(function (serviceFeature) {
    return Object.assign({}, serviceFeature, {
      BillerTypeAmounts: getActiveBillerTypeAmounts(serviceFeature.BillerTypeAmounts)
    });
  });
});
export var SelectedServiceAssociatedServiceAddonsSelector = createImmutableSelector([SelectedServiceAssociatedServiceFeaturesSelector], function (serviceFeatures) {
  return (serviceFeatures || []).filter(function (serviceFeature) {
    return !serviceFeature.ServiceAttributeValues.some(function (attribute) {
      return attribute.ServiceAttributeId === DEVICE_ADDON_SERVICE_ATTRIBUTE_ID;
    });
  });
});
export var SelectedServiceAssociatedDeviceAddonsSelector = createImmutableSelector([SelectedServiceAssociatedServiceFeaturesSelector], function (serviceFeatures) {
  return (serviceFeatures || []).filter(function (serviceFeature) {
    return serviceFeature.ServiceAttributeValues.some(function (attribute) {
      return attribute.ServiceAttributeId === DEVICE_ADDON_SERVICE_ATTRIBUTE_ID;
    });
  });
});
export var SelectedServiceAssociatedServiceIdSelector = createImmutableSelector([SelectedServiceIdentifierSelector, SelectedServiceAssociatedOfferDetailsSelector], function (serviceIdentifier, offer) {
  return offer && offer.Options.reduce(function (serviceId, option) {
    var selectedService = option.ServiceAttributeValues.find(function (service) {
      return service.Value === serviceIdentifier;
    });
    serviceId = selectedService && selectedService.ServiceId ? selectedService.ServiceId : serviceId;
    return serviceId;
  }, EMPTY_ARRAY);
});
export var SelectedServiceFormattedServiceIdentifierValueSelector = createImmutableSelector([SelectedServiceDetailsSelector, MetadataCodeTypeDictionarySelector(CODES.ServiceAttribute), MetadataCodeTypeDictionarySelector(CODES.RegularExpression)], function (selectedService, serviceAttributes, regularExpressions) {
  return selectedService && getFormattedServiceAttributeValue(selectedService.ServiceIdentifier.ServiceAttributeId, selectedService.ServiceIdentifier.Value, serviceAttributes, regularExpressions);
});
export var SharedEntitlementBalancesWithFormattedServiceIdentifierSelector = createImmutableSelector([SelectedServiceDetailsSelector, MetadataCodeTypeDictionarySelector(CODES.ServiceAttribute), MetadataCodeTypeDictionarySelector(CODES.RegularExpression)], function (selectedService, serviceAttributes, regularExpressions) {
  return selectedService && selectedService.SharedEntitlementBalances.map(function (entitlement) {
    var owningService = entitlement.EntitlementIdentifier && entitlement.EntitlementIdentifier.OwningServiceIdentifier;
    if (owningService) {
      entitlement = entitlement.setIn(['EntitlementIdentifier', 'OwningServiceIdentifier', 'FormattedValue'], getFormattedServiceAttributeValue(owningService.ServiceAttributeId, owningService.Value, serviceAttributes, regularExpressions));
    }
    return entitlement;
  }) || [];
});